// MaturaSetsProductSumSignOpenTask.js
// Zadanie maturalne – otwarte (0–2 pkt)
// Prawdopodobieństwo dla dwóch skończonych zbiorów X, Y (ciągi arytm. krok 1),
// warunek na znak iloczynu lub sumy: x·y (>,<,>=,<=) 0 lub x + y (>,<,>=,<=) 0.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import ProbabilityOutcomeTable from "../../../../components/ui/ProbabilityOutcomeTable";


/* ===== ułamki ===== */

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function simplifyFraction(num, den) {
  if (den === 0) return [num, den];
  const sign = den < 0 ? -1 : 1;
  const d = gcd(num, den);
  return [sign * (num / d), sign * (den / d)];
}

function parseFraction(raw) {
  if (raw == null) return null;
  const s = String(raw).replace(",", ".").trim();
  if (!s) return null;

  // całkowita
  if (/^[+-]?\d+$/.test(s)) {
    return [parseInt(s, 10), 1];
  }

  // a/b
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const d = parseInt(m[2], 10);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  return simplifyFraction(n, d);
}

function dfrac(n, d) {
  const [nn, dd] = simplifyFraction(n, d);
  return dd === 1 ? `${nn}` : `\\dfrac{${nn}}{${dd}}`;
}

function formatProbLatex(frac) {
  if (!frac) return "";
  const [n, d] = frac;
  return `P(A) = ${dfrac(n, d)}`;
}

/* ===== pomocnicze losowanie ===== */

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ===== tabele dla par (x,y) ===== */

function PairsTable({ X, Y, favourableKeys, displayMode }) {
  return (
    <ProbabilityOutcomeTable
      rows={X}
      columns={Y}
      getCellValue={(x, y) => displayMode === "prod" ? x * y : x + y}
      isHighlighted={(_x, _y, rowIndex, columnIndex) => favourableKeys.has(`${rowIndex}-${columnIndex}`)}
      ariaLabel={displayMode === "prod" ? "Tabela iloczynów par" : "Tabela sum par"}
    />
  );
}

/* ===== generowanie zbiorów X i Y ===== */

function buildConsecutiveSet(length) {
  // elementy w [-4,4], ciąg arytm. różnica 1, min ujemny, max dodatni
  const sets = [];
  const minStart = -4;
  const maxStart = 4 - (length - 1);

  for (let start = minStart; start <= maxStart; start += 1) {
    const arr = [];
    for (let k = 0; k < length; k += 1) {
      arr.push(start + k);
    }
    const hasNeg = arr.some((v) => v < 0);
    const hasPos = arr.some((v) => v > 0);
    if (!hasNeg || !hasPos) continue;
    sets.push(arr);
  }

  if (sets.length === 0) {
    const base = [-4, -3, -2, -1, 0, 1, 2, 3, 4];
    return base.slice(0, length);
  }

  return randomChoice(sets);
}

/* ===== warunki na sumę/iloczyn ===== */

const REL_OPTIONS = [
  { key: "gt", latex: ">", text: "większy od 0" },
  { key: "lt", latex: "<", text: "mniejszy od 0" },
  { key: "ge", latex: "\\ge", text: "nie mniejszy od 0" },
  { key: "le", latex: "\\le", text: "nie większy od 0" },
];

function makePredicate(kind, relKey) {
  return (pair) => {
    const v = kind === "prod" ? pair.prod : pair.sum;
    if (relKey === "gt") return v > 0;
    if (relKey === "lt") return v < 0;
    if (relKey === "ge") return v >= 0;
    if (relKey === "le") return v <= 0;
    return false;
  };
}

function setToLatex(arr) {
  return `\\{${arr.join(", ")}\\}`;
}

/* ===== generowanie całego zadania ===== */

function generateOne() {
  // długości zbiorów 3–7, NIERÓWNE (unikamy układów kwadratowych)
  const lenOptions = [3, 4, 5, 6, 7];
  const lenX = randomChoice(lenOptions);
  let lenY = randomChoice(lenOptions);
  while (lenY === lenX) {
    lenY = randomChoice(lenOptions);
  }

  const X = buildConsecutiveSet(lenX);
  const Y = buildConsecutiveSet(lenY);

  const allPairs = [];
  for (let i = 0; i < X.length; i += 1) {
    for (let j = 0; j < Y.length; j += 1) {
      const x = X[i];
      const y = Y[j];
      allPairs.push({
        i,
        j,
        x,
        y,
        sum: x + y,
        prod: x * y,
      });
    }
  }

  const total = allPairs.length;

  const kinds = ["prod", "sum"];
  const candidates = [];

  kinds.forEach((kind) => {
    REL_OPTIONS.forEach((rel) => {
      const pred = makePredicate(kind, rel.key);
      const favourablePairs = allPairs.filter(pred);
      const favCount = favourablePairs.length;
      if (favCount > 0 && favCount < total) {
        const exprLatex = kind === "prod" ? "x \\cdot y" : "x + y";
        const latexCondition = `${exprLatex} ${rel.latex} 0`;

        candidates.push({
          kind,
          relKey: rel.key,
          relText: rel.text,
          latexCondition,
          displayMode: kind,
          favourablePairs,
          favCount,
        });
      }
    });
  });

  if (candidates.length === 0) {
    return generateOne();
  }

  const chosen = randomChoice(candidates);
  const [num, den] = simplifyFraction(chosen.favCount, total);

  // UWAGA: wszystko wkładamy do data, żeby UniversalQuizRunner
  // mógł wywoływać renderExplanation({ data }) tak jak w statystyce.
  return {
    type: "MaturaSetsProductSumSignOpenTask",
    data: {
      X,
      Y,
      maxPoints: 2,
      sizeX: X.length,
      sizeY: Y.length,
      omegaSize: total,
      correctFraction: [num, den],
      favCount: chosen.favCount,
      exprKind: chosen.kind,
      relKey: chosen.relKey,
      relText: chosen.relText,
      latexCondition: chosen.latexCondition,
      displayMode: chosen.displayMode,
      favourablePairs: chosen.favourablePairs,
    },
  };
}

/* ===== API ===== */

function generateJune2025Original() {
  const X = [-3, -2, -1, 0, 1, 2];
  const Y = [-2, -1, 0, 1];
  const favourablePairs = [];

  X.forEach((x, i) => {
    Y.forEach((y, j) => {
      if (x * y >= 0) {
        favourablePairs.push({ i, j, x, y, sum: x + y, prod: x * y });
      }
    });
  });

  return {
    type: "MaturaSetsProductSumSignOpenTask",
    data: {
      X,
      Y,
      maxPoints: 2,
      sizeX: 6,
      sizeY: 4,
      omegaSize: 24,
      correctFraction: [17, 24],
      favCount: 17,
      exprKind: "prod",
      relKey: "ge",
      relText: "nie mniejszy od 0",
      latexCondition: "x \\cdot y \\ge 0",
      displayMode: "prod",
      favourablePairs,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 27,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  return generateOne();
}

/* ===== treść zadania ===== */

export function renderTask({ data }) {
  const { X, Y, latexCondition } = data;
  const setXLatex = setToLatex(X);
  const setYLatex = setToLatex(Y);

  return (
    <>
      <p>
        Dane są dwa zbiory:{" "}
        <InlineMath math={`X = ${setXLatex}`} /> oraz{" "}
        <InlineMath math={`Y = ${setYLatex}`} />.
      </p>
      <p>
        Losujemy jedną liczbę ze zbioru{" "}
        <InlineMath math={"X"} />, a następnie losujemy jedną liczbę ze
        zbioru <InlineMath math={"Y"} /> i tworzymy uporządkowaną parę liczb{" "}
        <InlineMath math={"(x, y)"} />, gdzie{" "}
        <InlineMath math={"x"} /> jest liczbą wylosowaną ze zbioru{" "}
        <InlineMath math={"X"} />, a{" "}
        <InlineMath math={"y"} /> jest liczbą wylosowaną ze zbioru{" "}
        <InlineMath math={"Y"} />.
      </p>
      <p>
        Oblicz prawdopodobieństwo zdarzenia{" "}
        <InlineMath math={"A"} /> polegającego na tym, że wylosowana para
        liczb <InlineMath math={"(x, y)"} /> spełnia warunek{" "}
        <InlineMath math={latexCondition} />. Zapisz obliczenia.
      </p>
    </>
  );
}

/* ===== INPUT: 3 pola (FractionInput) ===== */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (val) => onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: "grid", rowGap: 12, maxWidth: 340 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"|\\Omega| ="} />
        <FractionInput
          value={ua.omega}
          onChange={setField("omega")}
          disabled={disabled}
          label="|\Omega|"
          placeholder="np. 16"
        />
      </div>

      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"|A| ="} />
        <FractionInput
          value={ua.A}
          onChange={setField("A")}
          disabled={disabled}
          label="|A|"
          placeholder="np. 9"
        />
      </div>

      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"P(A) ="} />
        <FractionInput
          value={ua.prob}
          onChange={setField("prob")}
          disabled={disabled}
          label="P(A)"
          placeholder="np. 9/16"
        />
      </div>
    </div>
  );
}

/* ===== sprawdzanie (0 / 1 / 2 pkt) ===== */

function fieldFractionEqualsInt(fieldVal, targetInt) {
  let parsed = null;

  if (fieldVal && typeof fieldVal === "object" && fieldVal.raw != null) {
    parsed = parseFraction(fieldVal.raw);
  } else if (
    typeof fieldVal === "string" ||
    typeof fieldVal === "number"
  ) {
    parsed = parseFraction(fieldVal);
  }

  if (!parsed) return false;
  const [n, d] = simplifyFraction(parsed[0], parsed[1]);
  return d === 1 && n === targetInt;
}

function fieldFractionEqualsFrac(fieldVal, targetFrac) {
  let parsed = null;

  if (fieldVal && typeof fieldVal === "object" && fieldVal.raw != null) {
    parsed = parseFraction(fieldVal.raw);
  } else if (
    typeof fieldVal === "string" ||
    typeof fieldVal === "number"
  ) {
    parsed = parseFraction(fieldVal);
  }

  if (!parsed) return false;
  const [n1, d1] = simplifyFraction(parsed[0], parsed[1]);
  const [n2, d2] = simplifyFraction(targetFrac[0], targetFrac[1]);
  return n1 === n2 && d1 === d2;
}

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { omegaSize, favCount, correctFraction } = data;

  const omegaOk = fieldFractionEqualsInt(ua.omega, omegaSize);
  const aOk = fieldFractionEqualsInt(ua.A, favCount);
  const probOk = fieldFractionEqualsFrac(ua.prob, correctFraction);

  const correctCount = [omegaOk, aOk, probOk].filter(Boolean).length;
  const score = correctCount === 3 ? 2 : correctCount > 0 ? 1 : 0;

  return {
    isCorrect: omegaOk && aOk && probOk,
    correct: omegaOk && aOk && probOk,
    score,
    maxScore: 2,
    parts: {
      "|Ω|": omegaOk,
      "|A|": aOk,
      "P(A)": probOk,
    },
    expected: {
      omega: String(omegaSize),
      A: String(favCount),
      prob: formatProbLatex(correctFraction),
    },
  };
}

/* ===== odpowiedź modelowa (krótka) ===== */

export function renderAnswer({ data }) {
  const { omegaSize, favCount, correctFraction } = data;
  const [n, d] = correctFraction;

  return (
    <div style={{ display: "grid", rowGap: 4, marginTop: 12 }}>
      <InlineMath math={`|\\Omega| = ${omegaSize}`} />
      <InlineMath math={`|A| = ${favCount}`} />
      <InlineMath math={`P(A) = ${dfrac(n, d)}`} />
    </div>
  );
}

/* ===== odpowiedź użytkownika ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const getLatex = (raw) => {
    if (!raw) return "\\text{--}";

    const parsed = parseFraction(raw);
    if (parsed) {
      const [n, d] = parsed;
      return dfrac(n, d);
    }

    if (/^[+-]?\d+$/.test(String(raw).trim())) {
      return String(raw).trim();
    }

    return String(raw);
  };

  const omegaLatex = getLatex(userAnswer.omega?.raw ?? userAnswer.omega);
  const ALatex = getLatex(userAnswer.A?.raw ?? userAnswer.A);
  const probLatex = getLatex(userAnswer.prob?.raw ?? userAnswer.prob);

  return (
    <div style={{ display: "grid", rowGap: 4, marginTop: 12 }}>
      <InlineMath math={`|\\Omega| = ${omegaLatex}`} />
      <InlineMath math={`|A| = ${ALatex}`} />
      <InlineMath math={`P(A) = ${probLatex}`} />
    </div>
  );
}

/* ===== wyjaśnienie ===== */

export function renderExplanation({ data }) {
  const {
    X,
    Y,
    sizeX,
    sizeY,
    omegaSize,
    favCount,
    exprKind,
    latexCondition,
    displayMode,
    favourablePairs,
    correctFraction,
  } = data;

  const favourableKeys = new Set(
    favourablePairs.map((p) => `${p.i}-${p.j}`)
  );

  const [n, d] = correctFraction;
  const setXLatex = setToLatex(X);
  const setYLatex = setToLatex(Y);
  const exprLatex =
    exprKind === "prod" ? "x \\cdot y" : "x + y";

  return (
    <>
      <p>
        <strong>Krok 1.</strong> Zapisz przestrzeń zdarzeń.
      </p>
      <p>
        Mamy zbiory{" "}
        <InlineMath math={`X = ${setXLatex}`} /> oraz{" "}
        <InlineMath math={`Y = ${setYLatex}`} />. Losujemy niezależnie{" "}
        <InlineMath math={"x"} /> ze zbioru{" "}
        <InlineMath math={"X"} /> i{" "}
        <InlineMath math={"y"} /> ze zbioru{" "}
        <InlineMath math={"Y"} />, więc wszystkich możliwych uporządkowanych
        par <InlineMath math={"(x, y)"} /> jest
      </p>
      <BlockMath
        math={`|\\Omega| = |X| \\cdot |Y| = ${sizeX} \\cdot ${sizeY} = ${omegaSize}.`}
      />

      <p>
        <strong>Krok 2.</strong> Zapisz zdarzenie{" "}
        <InlineMath math={"A"} />.
      </p>

      <p>
        Warunek ma postać{" "}
        <InlineMath math={latexCondition} />.
      </p>

      <p>
        W tabeli poniżej w wierszach wypisujemy wartości{" "}
        <InlineMath math={"x \\in X"} />, a w kolumnach wartości{" "}
        <InlineMath math={"y \\in Y"} />. W każdej kratce wpisujemy{" "}
        {exprKind === "prod" ? "iloczyn " : "sumę "}
        <InlineMath math={exprLatex} />, a kratki spełniające warunek zaznaczamy.
      </p>

      <PairsTable
        X={X}
        Y={Y}
        favourableKeys={favourableKeys}
        displayMode={displayMode === "sum" ? "sum" : "prod"}
      />

      <p>
        Zaznaczonych par (czyli elementów zdarzenia{" "}
        <InlineMath math={"A"} />) jest{" "}
        <InlineMath math={String(favCount)} />, więc{" "}
        <InlineMath math={"|A| = "} />
        <InlineMath math={String(favCount)} />.
      </p>

      <p>
        <strong>Krok 3.</strong> Oblicz prawdopodobieństwo zdarzenia{" "}
        <InlineMath math={"A"} />.
      </p>

      <BlockMath
        math={`P(A) = \\dfrac{|A|}{|\\Omega|} = \\dfrac{${favCount}}{${omegaSize}}`}
      />
      <BlockMath math={`P(A) = ${dfrac(n, d)}`} />

      <p>
        Zatem prawdopodobieństwo zdarzenia{" "}
        <InlineMath math={"A"} /> jest równe{" "}
        <InlineMath math={dfrac(n, d)} />.
      </p>
    </>
  );
}
